// File intake: validation, size limit, and orchestration of the parse worker / mesh path.
import type { OcctImportParams, OcctResult, ParseRequest, ParseResponse } from '../types';
import { buildBoundsTrees } from '../bvh';
import { refreshShadedEdges } from '../display';
import { buildModel, type BuildStats, type ModelTreeNode } from '../model-builder';
import { clearMeasurement, clearTopologyCache } from '../measure';
import { setModelTree } from '../model-tree';
import { stopSection } from '../section';
import { initExplode, resetExplode } from '../explode';
import { rememberRecentFile } from '../recent-files';
import { defaultUnitFor, mmPerUnit, setCurrentModelUnit } from '../model-units';
import { dom, formatSize, log, onLengthUnitChange, setLoading, showAlert, showChoice } from '../ui';
import { fitCameraToModel, getModelBoundingBox, setModel } from '../viewer';
import * as THREE_NS from 'three';
import { connectWamp, isWampConnected, wampTeardown } from '../wamp';
import type * as THREE from 'three';
import { MAX_FILE_BYTES, MESH_EXTENSIONS, SUPPORTED_FORMATS, checkFile, formatMb } from './file-check';
import { peekGlbTextures, stripGlbTextures } from './glb-textures';

export { MAX_FILE_BYTES };

export type LoadFileOptions = {
  /** Optional File System Access handle so the file can be reopened from Recents. */
  handle?: FileSystemFileHandle;
};

const IMPORT_PARAMS: OcctImportParams = {
  linearUnit: 'millimeter',
  linearDeflectionType: 'bounding_box_ratio',
  linearDeflection: 0.0004, // default 0.001 (0.1% of bbox) -> finer
  angularDeflection: 0.15   // default 0.5 rad (~28.6°) -> ~8.6°, much smoother curves
};

// ---------------------------------------------------------------------------
// Worker plumbing
// ---------------------------------------------------------------------------

let worker: Worker | null = null;
let nextRequestId = 1;
let activeRequestId: number | null = null;
let activeReject: ((err: Error) => void) | null = null;

function ensureWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./parse.worker.ts', import.meta.url), { type: 'module' });
  }
  return worker;
}

/**
 * The Emscripten heap only grows; after a large STEP file it would keep
 * hundreds of MB. Terminating the worker after each parse gives that back
 * (the WASM is cached, so the next parse only pays a short re-init).
 */
function releaseWorker(): void {
  worker?.terminate();
  worker = null;
}

/** Terminates the worker: the only reliable way to stop OpenCascade mid-parse. */
export function cancelLoad(): void {
  if (!worker || activeRequestId === null) return;
  worker.terminate();
  worker = null;
  const reject = activeReject;
  activeRequestId = null;
  activeReject = null;
  setLoading(false);
  log('Parsing cancelled.', true);
  reject?.(new Error('Parsing cancelled.'));
}

export function isParsing(): boolean {
  return activeRequestId !== null;
}

function parseInWorker(file: File, buffer: ArrayBuffer, onProgress: (msg: string) => void): Promise<OcctResult> {
  const active = ensureWorker();
  const id = nextRequestId++;
  activeRequestId = id;

  return new Promise<OcctResult>((resolve, reject) => {
    activeReject = reject;

    const finish = (): void => {
      active.removeEventListener('message', onMessage);
      active.removeEventListener('error', onError);
      if (activeRequestId === id) { activeRequestId = null; activeReject = null; }
    };

    const onMessage = (event: MessageEvent<ParseResponse>): void => {
      const msg = event.data;
      if (!msg || msg.id !== id) return;
      if (msg.type === 'progress') {
        onProgress(msg.message);
      } else if (msg.type === 'result') {
        finish();
        releaseWorker();
        resolve(msg.result);
      } else {
        finish();
        releaseWorker();
        reject(new Error(msg.message));
      }
    };

    const onError = (event: ErrorEvent): void => {
      finish();
      reject(new Error(event.message || 'unknown error in the parse worker'));
    };

    active.addEventListener('message', onMessage);
    active.addEventListener('error', onError);

    const request: ParseRequest = {
      type: 'parse',
      id,
      name: file.name,
      buffer,
      isIges: /\.(igs|iges)$/i.test(file.name),
      params: IMPORT_PARAMS
    };
    active.postMessage(request, [buffer]);
  });
}

/** Overall size (X × Y × Z) of the freshly loaded, unexploded model. */
/** Re-read the model's size; call it after anything that rescales the model. */
export function refreshModelDims(): void {
  const box = getModelBoundingBox();
  if (!box || box.isEmpty()) {
    dom.modelMeta.classList.add('hidden');
    return;
  }
  lastModelSize = box.getSize(new THREE_NS.Vector3());
  renderModelDims();
}

let lastModelSize: THREE_NS.Vector3 | null = null;

const loadListeners: Array<() => void> = [];

/** Runs after a model has been loaded and the viewer has been updated. */
export function onModelLoaded(cb: () => void): void {
  loadListeners.push(cb);
}

/** Longest side in mm, for the unit plausibility check. 0 without a model. */
export function modelLongestSideMm(): number {
  if (!lastModelSize) return 0;
  return Math.max(lastModelSize.x, lastModelSize.y, lastModelSize.z);
}

function renderModelDims(): void {
  if (!lastModelSize) return;
  dom.modelDims.textContent = formatSize(lastModelSize.x, lastModelSize.y, lastModelSize.z);
  dom.modelMeta.classList.remove('hidden');
}

onLengthUnitChange(renderModelDims);

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/** One load at a time: a second file would race the first over the same state. */
let loadInProgress = false;

export async function loadFile(file: File, options?: LoadFileOptions): Promise<boolean> {
  if (loadInProgress) {
    showAlert('Another file is still being opened. Wait for it to finish, or cancel it first.', {
      title: 'Already opening a file'
    });
    return false;
  }

  const check = checkFile(file);
  if (check.kind === 'blocked') {
    showAlert(check.message, { detail: SUPPORTED_FORMATS });
    return false;
  }
  let dropTextures = false;
  if (check.kind === 'oversize') {
    // Claim the slot while the dialog is up: otherwise a second file dropped
    // behind it would walk straight past the one-load-at-a-time guard.
    loadInProgress = true;
    // Textures, not triangles, are what exhausts a phone: each one is
    // uncompressed to RGBA on the GPU. If this file has them, leaving them
    // out is a real third answer, so find out before asking.
    const textures = /\.glb$/i.test(file.name) ? await peekGlbTextures(file) : null;
    const canDrop = !!textures && textures.images > 0;
    const answer = await showChoice(check.message, {
      title: 'This file is very large',
      detail: canDrop
        ? `${textures.images} textures (${formatMb(textures.imageBytes)}) have to be uncompressed into graphics memory, which is usually what a phone runs out of. Without them every shape is still there, but a surface that got its colour from a texture turns plain. ${check.detail}`
        : check.detail,
      confirmLabel: 'Open anyway',
      altLabel: canDrop ? 'Without textures' : undefined,
      cancelLabel: "Don't open"
    });
    loadInProgress = false;
    if (answer === 'cancel') {
      log(`Opening "${file.name}" cancelled — over the size limit.`, true);
      return false;
    }
    dropTextures = answer === 'alt';
    log(
      `Opening "${file.name}" (${formatMb(file.size)}) past the size limit, at the user's request` +
        (dropTextures ? ', without textures.' : '.'),
      true
    );
  }

  const isMesh = MESH_EXTENSIONS.test(file.name);
  loadInProgress = true;
  // Only the STEP/IGES worker can be cancelled; mesh loaders parse in one go.
  setLoading(true, isMesh ? `Loading ${file.name} …` : `Parsing file: ${file.name} …`, !isMesh);
  // A brand new model means a brand new camera position/scale via
  // fitCameraToModel() -- an abrupt teleport, not a gradual move. Feeding that
  // mid-session to a live driver connection is what appears to have crashed
  // 3DxNLServer, so disconnect first and reconnect fresh once the new camera
  // state has settled.
  const wasWampConnected = isWampConnected();
  if (wasWampConnected) wampTeardown(false);

  try {
    const buffer = await file.arrayBuffer();
    let group: THREE.Group;
    let stats: BuildStats;
    let tree: ModelTreeNode;

    if (dropTextures) {
      // Rewrites the JSON chunk in place; the geometry is never copied.
      const stripped = stripGlbTextures(buffer);
      log(stripped ? 'Textures left out of this model.' : 'No textures to leave out.', true);
    }

    if (isMesh) {
      const { loadMeshFromBuffer } = await import('./load-mesh');
      ({ group, stats, tree } = await loadMeshFromBuffer(buffer, file.name));
    } else {
      const result = await parseInWorker(file, buffer, msg => setLoading(true, msg));
      ({ group, stats, tree } = buildModel(result, file.name));
    }

    // The viewer measures in millimetres; scale the model so one of its units
    // means what the file's format says it means.
    const unit = defaultUnitFor(file.name);
    setCurrentModelUnit(unit);
    group.scale.setScalar(mmPerUnit(unit));

    clearTopologyCache();
    resetExplode();
    buildBoundsTrees(group);
    setModel(group);
    setModelTree(tree);
    refreshShadedEdges();
    clearMeasurement();
    stopSection();
    fitCameraToModel();
    initExplode();
    dom.explodeSlider.value = '0';
    dom.explodeValue.textContent = '0%';
    dom.modelTitle.textContent = file.name;
    dom.modelTitle.title = file.name;
    refreshModelDims();
    dom.dropZone.classList.add('hidden');
    document.body.classList.remove('no-model');
    log(
      `Model loaded: ${file.name} — ${stats.meshCount} mesh(es), ${stats.nodeCount} tree node(s), ${stats.materialCount} material(s)`,
      true
    );
    void rememberRecentFile(file, options?.handle);
    for (const cb of loadListeners) cb();
    if (wasWampConnected) setTimeout(connectWamp, 400);
    return true;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/cancelled/i.test(msg)) return false;
    showAlert(`"${file.name}" could not be read: ${msg}`, {
      title: 'Could not open this file',
      detail: 'The file may be damaged, incomplete, or saved in a variant this viewer does not read.'
    });
    return false;
  } finally {
    loadInProgress = false;
    setLoading(false);
  }
}
